import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TextField } from 'src/Libs/inputs/formik'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/material'
import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { object, string } from 'yup'
import Select from 'src/Libs/inputs/formik/Select'
import { useShopCart } from 'src/Libs/shop/hooks'
import { AxiosError } from 'axios'
import { CountriesModelResponse } from 'src/Libs/shop/models'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { REQUIRED_SYMBOL } from 'src/Configuration'

export interface NewShippingAddressModalProps {
  place: number
  onClose: () => void
  onConfirm: (companyName: string, newAddress: string, permanent: boolean, onClose: () => void) => Promise<void> | void
}

interface ShippingAddressForm {
  street: string
  zipCode: string
  city: string
  country: string
  companyName: string
  companyNameAddition: string
  permanent: boolean
}

/*
 * This Component shows to the user required input fields that the Shop needs for a new Address for Shipping
 */

const NewShippingAddressModal: React.FC<NewShippingAddressModalProps> = ({ onClose, onConfirm, place }) => {
  const { t } = useCustomTranslation()
  const [countries, setCountries] = useState<CountriesModelResponse | null>(null)
  const { saveTemporaryShippingAddress, saveShippingAddress, getCountries } = useShopCart()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()

  useEffect(() => {
    getCountries()
      .then(result => {
        setCountries(result)
      })
      .catch((e: AxiosError) => {
        console.error(e)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (formData: ShippingAddressForm): Promise<void> => {
    const completedAddress = `${formData.street}, ${formData.zipCode} ${formData.city}`
    const companyName = formData.companyName
    const isPermament = Boolean(formData.permanent)
    const payload = {
      street: formData.street,
      zipCode: formData.zipCode,
      city: formData.city,
      countryIso2: formData.country,
      companyName: formData.companyName,
      companyNameAddition: formData.companyNameAddition,
    }

    try {
      if (formData.permanent === true) {
        await Promise.all([saveTemporaryShippingAddress(payload), saveShippingAddress(payload)])
        onConfirm(companyName, completedAddress, isPermament, onClose)
        enqueueSuccessStack(t('shop.new-shipping-address.success-message'))
      } else {
        await saveTemporaryShippingAddress(payload)
        onConfirm(companyName, completedAddress, isPermament, onClose)
        enqueueSuccessStack(t('shop.new-shipping-address.success-message'))
      }
    } catch (error) {
      console.error(error)
      enqueueErrorStack(t('shop.new-shipping-address.error-message'))
    }
  }

  const initialValues = { street: '', zipCode: '', country: '', companyName: '', companyNameAddition: '', permanent: false } as ShippingAddressForm
  const validationSchema = object().shape({
    street: string().required(t('validations.form.street.is-required')),
    zipCode: string().required(t('validations.form.zipCode.is-required')),
    city: string().required(t('validations.form.city.is-required')),
    country: string().required(t('validations.form.country.is-required')),
    companyName: string().required(t('validations.form.companyName.is-required')).max(50, t('validations.form.companyName.max-length')),
    companyNameAddition: string().max(50, t('validations.form.companyNameAddition.max-length')),
  })

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} place={place}>
      {({ isValid, dirty, handleSubmit }) => (
        <Dialog
          title={t('shop.new-shipping.address')}
          onClose={onClose}
          actions={{
            primary: {
              name: t('common.save'),
              disabled: !isValid && !dirty,
              onClick: handleSubmit,
            },
            secondary: {
              name: t('common.cancel'),
              onClick: () => {
                onClose()
              },
            },
          }}
          maxWidth="sm"
        >
          <form onSubmit={handleSubmit}>
            {countries && (
              <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2 }}>
                <TextField required={true} name={'street'} label={t('shop.new-shipping.street')} />
                <TextField required={true} name={'zipCode'} label={t('shop.new-shipping.zip')} />
                <TextField required={true} name={'city'} label={t('shop.new-shipping.city')} />
                <Select
                  name={'country'}
                  label={`${t('shop.new-shipping.country')} ${REQUIRED_SYMBOL}`}
                  options={countries.elements
                    .sort((a, b) => a.position - b.position)
                    .map(country => ({ label: country.translated.name, value: country.iso }))}
                ></Select>
                <TextField required={true} name={'companyName'} label={t('shop.new-shipping.company-name')} />
                <TextField name={'companyNameAddition'} label={t('shop.new-shipping.company-name-addition')} />
                {/* 
                TODO: Permament address disabled, will have a refactor (this code will be used later again) 
                <FormGroup>
                  <FormControlLabel control={<Checkbox name={'permanent'}></Checkbox>} label={t('shop.new-shipping.save-for-later')} />
                </FormGroup> */}
              </Box>
            )}
          </form>
        </Dialog>
      )}
    </Formik>
  )
}

export default NewShippingAddressModal
